import React, { useState, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import { groupByAssessmentRound } from "../utils/assessmentRoundGroups";
import {
  CheckCircle, UserPlus, UserMinus, RefreshCw, ChevronDown, ChevronUp, Mail, Bell,
  AlertTriangle, Send, FilePlus, Undo2, CheckSquare, Edit3, Flag, Paperclip, Trash2,
} from "lucide-react";

// valueChange: true marks the per-question actions that carry questionId/previousValue/newValue
// instead of an assignee — rendered via the fallback branch below, never assignedToUserName.
const ACTION_CONFIG = {
  assigned:                { icon: UserPlus,      color: "text-blue-500",   label: "Assigned to" },
  reassigned:               { icon: RefreshCw,     color: "text-amber-500",  label: "Reassigned to" },
  completed:                { icon: CheckCircle,   color: "text-green-500",  label: "Completed by" },
  created:                  { icon: FilePlus,      color: "text-blue-500",   label: "Created by", performerOnly: true },
  respondent_added:         { icon: UserPlus,      color: "text-cyan-600",   label: "Respondent added:" },
  respondent_removed:       { icon: UserMinus,     color: "text-gray-500",   label: "Respondent removed:" },
  invite_sent:              { icon: Mail,          color: "text-cyan-600",   label: "Invite sent to" },
  reminder_sent:            { icon: Bell,          color: "text-amber-500",  label: "Reminder sent to" },
  escalation_sent:          { icon: AlertTriangle, color: "text-red-500",    label: "Escalated to risk managers", performerOnly: true },
  submitted:                { icon: Send,          color: "text-green-600",  label: "Submitted by", performerOnly: true },
  reopened:                 { icon: Undo2,         color: "text-amber-600",  label: "Reopened by", performerOnly: true },
  auto_reassessment:        { icon: RefreshCw,     color: "text-purple-500", label: "Reassessment auto-issued", performerOnly: true },
  deleted:                  { icon: Trash2,        color: "text-red-500",    label: "Deleted by", performerOnly: true },
  // On the restored round: its reassessment (newValue = that round's date, yyyy-MM-dd) was deleted.
  reassessment_deleted:     { icon: Trash2,        color: "text-red-500",    label: "Reassessment deleted:" },
  question_answered:        { icon: CheckSquare,   color: "text-blue-500",   label: "Answered", valueChange: true },
  question_answer_changed:  { icon: Edit3,         color: "text-amber-500",  label: "Answer changed", valueChange: true },
  question_cleared:         { icon: Edit3,         color: "text-gray-400",   label: "Answer cleared (question hidden)", valueChange: true },
  note_added:               { icon: Edit3,         color: "text-cyan-600",   label: "Note added", valueChange: true },
  note_changed:             { icon: Edit3,         color: "text-amber-500",  label: "Note changed", valueChange: true },
  flagged:                  { icon: Flag,          color: "text-red-500",    label: "Flagged", valueChange: true },
  unflagged:                { icon: Flag,          color: "text-gray-400",   label: "Unflagged", valueChange: true },
  document_uploaded:        { icon: Paperclip,     color: "text-cyan-600",   label: "Document uploaded", valueChange: true },
  document_removed:         { icon: Trash2,        color: "text-gray-500",   label: "Document removed", valueChange: true },
  document_type_changed:    { icon: RefreshCw,     color: "text-amber-500",  label: "Document type changed", valueChange: true },
};

const formatDate = (ts) =>
  new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const truncate = (s, n = 60) =>
  !s ? s : s.length > n ? `${s.slice(0, n)}…` : s;

const ActivityEntry = ({ entry }) => {
  // Unlike before, an unrecognized action no longer silently borrows "assigned"'s icon/label —
  // that was the actual bug (every per-question log entry rendered as "Assigned to ?" once a
  // config lookup fell through). Anything still not in the map falls back to a neutral display
  // instead of masquerading as an assignment.
  const cfg = ACTION_CONFIG[entry.action] ?? { icon: Edit3, color: "text-gray-400", label: entry.action ?? "Activity" };
  const Icon = cfg.icon;

  let detail = "";
  if (entry.action === "completed") {
    detail = entry.performedByUserName;
  } else if (entry.action === "reassigned") {
    detail = `${entry.previousAssigneeUserName ?? "?"} → ${entry.assignedToUserName ?? "?"} (by ${entry.performedByUserName})`;
  } else if (cfg.performerOnly) {
    // Actions where the performer IS the story (created, submitted, reopened, escalation, auto-reassessment)
    detail = entry.assignedToUserName
      ? `${entry.assignedToUserName} (by ${entry.performedByUserName})`
      : entry.performedByUserName;
  } else if (cfg.valueChange) {
    // Per-question events — no assignee at all, just what changed on which question.
    const question = entry.questionId != null ? `Q${entry.questionId}: ` : "";
    const prev = truncate(entry.previousValue);
    const next = truncate(entry.newValue);
    const change = prev && next ? `${prev} → ${next}` : next || prev || "";
    detail = `${question}${change} (by ${entry.performedByUserName})`;
  } else if (entry.action === "reassessment_deleted") {
    const of = entry.newValue ? `assessment of ${formatDate(`${entry.newValue}T00:00:00`)} ` : "";
    detail = `${of}(by ${entry.performedByUserName})`;
  } else if (entry.action === "assigned") {
    detail = `${entry.assignedToUserName ?? "?"} (by ${entry.performedByUserName})`;
  } else {
    detail = entry.performedByUserName;
  }

  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
      <div className="flex-1 min-w-0">
        <span className="text-xs text-gray-600">
          <span className="font-medium text-gray-700">{cfg.label}</span>{" "}
          {detail}
        </span>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(entry.timestamp)}</span>
    </div>
  );
};

// Per-question events (answers, notes, flags, question documents) — identified by carrying a
// questionId or a valueChange-flagged action. Main-page activity panels can hide these via
// hideQuestionEvents: they belong in the assessment / view-answers pages (QuestionHistoryToggle),
// while the card keeps whole-assessment activity (assigned/completed/submitted/…).
const isQuestionEvent = (e) =>
  e.questionId != null || ACTION_CONFIG[e.action]?.valueChange === true;

// includeChain: also load the log of every earlier reassessment round (Enterprise only — the
// endpoint ignores it for other types), grouped under "This assessment" / "Previous assessment".
// Enterprise reassess creates a new assessment row, so without it the card shows no history from
// before the latest reassessment.
const AssessmentActivityLog = ({ assessmentType, assessmentId, hideQuestionEvents = false, includeChain = false }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const apiCall = useApi();

  const load = useCallback(async () => {
    if (entries !== null) return;
    setLoading(true);
    try {
      const data = await apiCall(
        "GET",
        `/assessmentActivityLog?assessmentType=${assessmentType}&assessmentId=${assessmentId}${includeChain ? "&includeChain=true" : ""}`
      );
      const list = Array.isArray(data) ? data : [];
      setEntries(hideQuestionEvents ? list.filter((e) => !isQuestionEvent(e)) : list);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [assessmentType, assessmentId, entries, hideQuestionEvents, includeChain]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) load();
    setOpen((v) => !v);
  };

  const count = entries?.length ?? 0;

  return (
    <div>
      <button
        onClick={toggle}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Activity
        {entries !== null && count > 0 && (
          <span className="ml-1 px-1.5 py-0.5 bg-gray-100 rounded-full text-gray-600">{count}</span>
        )}
      </button>

      {open && (
        <div className="mt-1 pl-1 border-l-2 border-gray-100">
          {loading && <p className="text-xs text-gray-400 py-1">Loading…</p>}
          {!loading && entries?.length === 0 && (
            <p className="text-xs text-gray-400 py-1">No activity yet.</p>
          )}
          {!loading && entries?.length > 0 && groupByAssessmentRound(entries).map((group) => (
            <div key={group.key}>
              {group.label && (
                <div className="text-[11px] font-medium text-gray-500 pt-2 pb-0.5 border-b border-gray-100">{group.label}</div>
              )}
              {group.entries.map((e) => <ActivityEntry key={e.id} entry={e} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentActivityLog;
